/*
 * Wrapper over the ui Table implementing the CasesV2 standard: scroll
 * container, sticky chrome header, skeleton rows while loading, and
 * ErrorState for empty / error.
 */
import type { LucideIcon } from "lucide-react";
import {
  forwardRef,
  type CSSProperties,
  type ReactNode,
  type Ref,
  type UIEvent
} from "react";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { ErrorState } from "../../../../components/common/ErrorState";

/**
 * Sticky header cell. The background is the `chrome` token via its CSS
 * variable (not a literal RGB) so it follows the `.dark` seeds in
 * styles/tokens.css; inline styles never see Tailwind's `dark:` variant.
 */
export const STICKY_HEADER_STYLE = {
  position: "sticky" as const,
  top: 0,
  zIndex: 10,
  backgroundColor: "var(--chrome)"
};

/** Text classes shared by every client profile table. */
export const TABLE_TEXT = {
  primary: "text-base font-medium text-fg",
  body: "text-base text-fg",
  muted: "text-sm text-fg-3"
} as const;

export type ClientTableColumn = {
  id: string;
  header: ReactNode;
  /** Applied to the header cell's inner layout (alignment). */
  className?: string;
  /** Applied to the `<th>` (column widths). Merged over the sticky header. */
  style?: CSSProperties;
};

export type ClientTableEmpty = {
  title: string;
  message?: string;
  /** The section's icon, shown in the empty state's tile. */
  icon?: LucideIcon;
  /** A secondary action; the header already holds the primary one. */
  cta?: string;
  ctaIcon?: LucideIcon;
  onCta?: () => void;
  /** Replaces the default `cta` button (for a primary action or a menu). */
  action?: ReactNode;
  /** Rendered under the empty state (e.g. what the agent could read). */
  extra?: ReactNode;
};

type Props = {
  columns: ClientTableColumn[];
  isLoading: boolean;
  isEmpty: boolean;
  error?: string | null;
  onRetry?: () => void;
  empty: ClientTableEmpty;
  skeletonRows?: number;
  renderSkeletonRow?: (index: number) => ReactNode;
  /** Infinite-scroll hook-in on the scroll container. */
  onScroll?: (event: UIEvent<HTMLDivElement>) => void;
  /** Rendered after the rows (e.g. "Load more" / spinner row). */
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Extra classes on the `<table>` (for example `table-fixed`). */
  tableClassName?: string;
  ariaLabel?: string;
};

const DefaultSkeletonRow = ({ columns }: { columns: number }) => (
  <TableRow className="hover:bg-transparent">
    {Array.from({ length: columns }).map((_, i) => (
      <TableCell key={i} className="h-12">
        <Skeleton className={cn("h-3", i === 0 ? "w-40" : "w-24")} />
      </TableCell>
    ))}
  </TableRow>
);

const StateRow = ({
  columns,
  children
}: {
  columns: number;
  children: ReactNode;
}) => (
  <TableRow className="hover:bg-transparent">
    <TableCell colSpan={columns} className="h-full whitespace-normal">
      <div className="flex w-full items-center justify-center py-10">
        {children}
      </div>
    </TableCell>
  </TableRow>
);

export const ClientTable = forwardRef<HTMLDivElement, Props>(
  function ClientTable(
    {
      columns,
      isLoading,
      isEmpty,
      error,
      onRetry,
      empty,
      skeletonRows = 6,
      renderSkeletonRow,
      onScroll,
      footer,
      children,
      className,
      tableClassName,
      ariaLabel
    },
    ref: Ref<HTMLDivElement>
  ) {
    const showSkeleton = isLoading && isEmpty && !error;
    const showEmpty = !isLoading && isEmpty && !error;
    const stateTableClass = showEmpty || error ? "h-full" : undefined;

    let body: ReactNode;
    if (error) {
      body = (
        <StateRow columns={columns.length}>
          <ErrorState
            title="Something went wrong."
            message={error}
            buttonCta={onRetry ? "Retry" : ""}
            onRetry={onRetry}
            minH="auto"
          />
        </StateRow>
      );
    } else if (showSkeleton) {
      body = Array.from({ length: skeletonRows }).map((_, i) =>
        renderSkeletonRow ? (
          renderSkeletonRow(i)
        ) : (
          <DefaultSkeletonRow key={i} columns={columns.length} />
        )
      );
    } else if (showEmpty) {
      const CtaIcon = empty.ctaIcon;
      body = (
        <StateRow columns={columns.length}>
          <div className="flex w-full max-w-lg flex-col items-center gap-4">
            <EmptyState
              className="flex-none p-0"
              icon={empty.icon}
              title={empty.title}
              description={empty.message}
              action={
                empty.action ??
                (empty.cta && empty.onCta ? (
                  <Button variant="secondary" onClick={empty.onCta}>
                    {CtaIcon ? <CtaIcon /> : null}
                    {empty.cta}
                  </Button>
                ) : undefined)
              }
            />
            {empty.extra}
          </div>
        </StateRow>
      );
    } else {
      body = children;
    }

    return (
      <div
        ref={ref}
        onScroll={onScroll}
        data-testid="client-table-scroll"
        className={cn(
          "relative flex min-h-0 w-full flex-1 flex-col overflow-auto bg-surface",
          className
        )}
      >
        <Table
          containerClassName={cn("overflow-visible", stateTableClass)}
          className={cn("w-full", tableClassName, stateTableClass)}
          aria-label={ariaLabel}
        >
          <TableHeader className="[&_tr]:border-line-1">
            <TableRow>
              {columns.map((col) => (
                <TableHead
                  key={col.id}
                  className="h-auto p-0"
                  style={{ ...STICKY_HEADER_STYLE, ...col.style }}
                >
                  <div
                    className={cn(
                      "flex h-8 w-full items-center gap-1 px-3 text-sm font-medium text-fg-3",
                      col.className
                    )}
                  >
                    {col.header}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className="border-b border-line-1">
            {body}
            {!error && !showSkeleton && !showEmpty ? footer : null}
          </TableBody>
        </Table>
      </div>
    );
  }
);
